'use client'

import Link from 'next/link'
import parse from 'html-react-parser'
import { useSetAtom } from 'jotai'

import AppImage from '@/components/AppImage'
import { trackStateAtom } from '@/components/nav/primary/state'

import type { LinkElement } from '@/components/nav/primary/types'

type LinkListProps = {
  links: LinkElement[]
  className?: string
}

export function LinkList({ links, className = '' }: LinkListProps): JSX.Element {
  const setTrackVisible = useSetAtom(trackStateAtom)

  const recursiveLinkItems = links.map((item, index) => {
    const { link } = item

    const key = `link-${link.title}-${index}`
    const isHighlightedClass = item?.highlight ? 'font-bold' : 'font-normal'

    // Use the title if it exists, otherwise use the post name
    const title = link?.title && link?.title.length ? link.title : link?.name

    if (!title) {
      return null
    }

    return (
      <li key={key} className={isHighlightedClass}>
        <Link
          href={link.url}
          onClick={() => setTrackVisible(false)}
          className={`block py-6 text-17 leading-normal transition-[font-weight,color] hover:text-primary xl:text-15`}>
          <span>{parse(title)}</span>
        </Link>
      </li>
    )
  })

  return <ul className={`${className} list-reset`}>{recursiveLinkItems}</ul>
}

export function LinkCard({ image, link }): JSX.Element {
  const setTrackVisible = useSetAtom(trackStateAtom)
  const { url, alt } = image ?? {}

  return (
    <figure className="group">
      <Link href={link.url} prefetch={false} onClick={() => setTrackVisible(false)}>
        <AppImage
          path={url}
          alt={alt}
          width={322}
          height={200}
          className="overflow-hidden rounded-sm l:w-[220px] s:w-[180px]"
          transforms={{
            width: '322',
            height: '202',
            focus: 'auto',
            crop: 'maintain_ratio',
            dpr: '2',
          }}
        />
      </Link>

      <figcaption className="mt-12">
        <span className="block text-14 text-grey-800">Featured</span>

        <Link
          href={link.url}
          prefetch={false}
          onClick={() => setTrackVisible(false)}
          className="whitespace-nowrap text-17 font-bold leading-normal transition-colors hover:text-primary group-hover:text-primary xl:text-15">
          {parse(link.title)} &gt;
        </Link>
      </figcaption>
    </figure>
  )
}
